import { CommonModule } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core'
import { LazyLoadComponentConfig } from '@se-po/shared-util'
import { LazyLoadComponent, LazyLoadComponentOutputs } from './lazy-load.component'
import { Subscription } from 'rxjs'

type LazyLoadGroupConfig = Partial<Record<string, LazyLoadComponentConfig>>

const DEFAULT_INITIAL_LOAD_COUNT = 3
const DEFAULT_MAX_CONCURRENT_LOADS = 2
const DEFAULT_VIEWPORT_ROOT_MARGIN = '600px 0px'

@Component({
  selector: 'se-po-lazy-load-group',
  standalone: true,
  imports: [
    CommonModule,
    LazyLoadComponent
  ],
  template: `
    @for (module of currentModules; track module) {
      <div #moduleSlot [attr.data-lazy-load-module]="module">
        <se-po-lazy-load
          [active]="isModuleActive(module)"
          [hostReady]="hostReady"
          [loadKey]="resolvedLoadKey"
          [componentConfig]="moduleConfig(module)"
          [componentInputs]="componentInputs"
          [componentOutputs]="componentOutputs"
          (loadingChange)="onModuleLoadingChange(module, $event)"
          (loadingError)="onModuleLoadingError(module)">
        </se-po-lazy-load>
      </div>
    }
  `,
})
export class LazyLoadGroupComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChildren('moduleSlot', { read: ElementRef })
  private moduleSlots!: QueryList<ElementRef<HTMLElement>>

  @Input() public hostReady = true
  @Input() public modules: string[] = []
  @Input() public moduleConfigs: LazyLoadGroupConfig = {}
  @Input() public componentInputs: Record<string, unknown> = {}
  @Input() public componentOutputs: LazyLoadComponentOutputs = {}
  @Input() public initialLoadCount = DEFAULT_INITIAL_LOAD_COUNT
  @Input() public maxConcurrentLoads = DEFAULT_MAX_CONCURRENT_LOADS
  @Input() public viewportRootMargin = DEFAULT_VIEWPORT_ROOT_MARGIN
  @Input() public loadKey: unknown

  public currentModules: string[] = []
  public loadingModules = new Set<string>()
  public resolvedLoadKey: unknown

  private activatedModules = new Set<string>()
  private completedModules = new Set<string>()
  private queuedModules = new Set<string>()
  private observer?: IntersectionObserver
  private observerFrameId?: number
  private slotsSubscription?: Subscription
  private viewInitialized = false
  private loadKeyInitialized = false
  private elementRef = inject(ElementRef<HTMLElement>)
  private cdr = inject(ChangeDetectorRef)
  private ngZone = inject(NgZone)

  public ngAfterViewInit(): void {
    this.viewInitialized = true
    this.slotsSubscription = this.moduleSlots.changes.subscribe(() => this.observeModuleSlots())
    this.observeModuleSlots()
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const nextLoadKey = this.resolveLoadKey()
    const loadKeyChanged = this.loadKeyInitialized && !Object.is(this.resolvedLoadKey, nextLoadKey)
    this.resolvedLoadKey = nextLoadKey
    this.loadKeyInitialized = true

    if (changes.modules || changes.moduleConfigs || changes.initialLoadCount || loadKeyChanged) {
      this.resetCurrentModules(loadKeyChanged)
    }
    if (changes.maxConcurrentLoads && !changes.maxConcurrentLoads.firstChange) this.loadQueuedModules()
    if (changes.viewportRootMargin && !changes.viewportRootMargin.firstChange) this.observeModuleSlots()
  }

  public ngOnDestroy(): void {
    this.observer?.disconnect()
    if (this.observerFrameId !== undefined) cancelAnimationFrame(this.observerFrameId)
    this.slotsSubscription?.unsubscribe()
  }

  public moduleConfig(module: string): LazyLoadComponentConfig {
    return this.moduleConfigs[module] as LazyLoadComponentConfig
  }

  public onModuleLoadingChange(module: string, loading: boolean): void {
    if (loading) {
      if (!this.activatedModules.has(module)) return
      this.completedModules.delete(module)
      this.loadingModules.add(module)
      return
    }

    this.loadingModules.delete(module)
    this.completedModules.add(module)
    this.loadQueuedModules()
  }

  public onModuleLoadingError(module: string): void {
    this.loadingModules.delete(module)
    this.completedModules.delete(module)
    this.loadQueuedModules()
  }

  public isModuleActive(module: string): boolean {
    return this.activatedModules.has(module)
  }

  private resetCurrentModules(resetLoadState = false): void {
    const nextModules = this.modules.filter(module => Boolean(this.moduleConfigs[module]))
    const nextModuleSet = new Set(nextModules)

    this.currentModules = nextModules
    if (resetLoadState) {
      this.observer?.disconnect()
      this.resetViewportScroll()
      this.activatedModules.clear()
      this.completedModules.clear()
      this.loadingModules.clear()
      this.queuedModules.clear()
    } else {
      this.activatedModules = this.intersection(this.activatedModules, nextModuleSet)
      this.completedModules = this.intersection(this.completedModules, nextModuleSet)
      this.loadingModules = this.intersection(this.loadingModules, nextModuleSet)
      this.queuedModules = this.intersection(this.queuedModules, nextModuleSet)
    }

    nextModules.slice(0, Math.max(0, this.initialLoadCount)).forEach(module => {
      this.activatedModules.add(module)
      this.queuedModules.delete(module)
    })

    this.startActivatedModules()
    if (resetLoadState) {
      this.observeAfterScrollReset()
    } else {
      this.observeModuleSlots()
    }
  }

  private startActivatedModules(): void {
    this.activatedModules.forEach(module => {
      if (!this.completedModules.has(module)) this.loadingModules.add(module)
    })
    this.loadQueuedModules()
  }

  private observeModuleSlots(): void {
    if (!this.viewInitialized) return
    if (this.observerFrameId !== undefined) return

    this.observer?.disconnect()
    if (typeof IntersectionObserver === 'undefined') {
      this.activateAllModules()
      return
    }

    const observedLoadKey = this.resolvedLoadKey
    this.observer = new IntersectionObserver(
      entries => {
        if (!Object.is(observedLoadKey, this.resolvedLoadKey)) return
        this.ngZone.run(() => this.onModuleIntersection(entries))
      },
      {
        root: this.elementRef.nativeElement.closest('.se-fe-side-panel__wrapper'),
        rootMargin: this.viewportRootMargin,
      }
    )

    this.moduleSlots.forEach(slot => {
      const module = slot.nativeElement.dataset.lazyLoadModule
      if (module && !this.activatedModules.has(module)) this.observer?.observe(slot.nativeElement)
    })
  }

  private onModuleIntersection(entries: IntersectionObserverEntry[]): void {
    const intersectingModules = new Set(
      entries
        .filter(entry => entry.isIntersecting)
        .map(entry => (entry.target as HTMLElement).dataset.lazyLoadModule)
        .filter((module): module is string => Boolean(module))
    )

    this.currentModules.forEach(module => {
      if (intersectingModules.has(module) && !this.activatedModules.has(module)) {
        this.queuedModules.add(module)
      }
    })
    this.loadQueuedModules()
  }

  private loadQueuedModules(): void {
    const maxConcurrentLoads = Math.max(1, this.maxConcurrentLoads)
    while (this.queuedModules.size && this.loadingModules.size < maxConcurrentLoads) {
      const module = this.currentModules.find(candidate => this.queuedModules.has(candidate))
      if (!module) break

      this.queuedModules.delete(module)
      this.activatedModules.add(module)
      this.loadingModules.add(module)
      this.unobserveModule(module)
    }
    this.cdr.markForCheck()
  }

  private activateAllModules(): void {
    this.currentModules.forEach(module => this.activatedModules.add(module))
    this.queuedModules.clear()
    this.startActivatedModules()
    this.cdr.markForCheck()
  }

  private unobserveModule(module: string): void {
    const slot = this.moduleSlots.find(element => element.nativeElement.dataset.lazyLoadModule === module)
    if (slot) this.observer?.unobserve(slot.nativeElement)
  }

  private resetViewportScroll(): void {
    const viewport = this.viewport()
    if (viewport) viewport.scrollTop = 0
  }

  private observeAfterScrollReset(): void {
    if (!this.viewInitialized) return
    if (this.observerFrameId !== undefined) cancelAnimationFrame(this.observerFrameId)

    const observeWhenReset = (): void => {
      this.observerFrameId = undefined
      const viewport = this.viewport()
      if (viewport && viewport.scrollTop !== 0) {
        viewport.scrollTop = 0
        this.observerFrameId = requestAnimationFrame(observeWhenReset)
        return
      }

      this.observeModuleSlots()
    }

    this.observerFrameId = requestAnimationFrame(observeWhenReset)
  }

  private viewport(): HTMLElement | null {
    return this.elementRef.nativeElement.closest('.se-fe-side-panel__wrapper') as HTMLElement | null
  }

  private resolveLoadKey(): unknown {
    if (this.loadKey !== undefined) return this.loadKey

    const contextIds = [
      this.componentInputs?.['personaId'],
      this.componentInputs?.['orgId'],
      this.componentInputs?.['inviteId'],
    ]
    if (contextIds.every(value => value === null || value === undefined)) return undefined

    return JSON.stringify(contextIds.map(value => value === null || value === undefined ? null : String(value)))
  }

  private intersection(values: Set<string>, allowedValues: Set<string>): Set<string> {
    return new Set([...values].filter(value => allowedValues.has(value)))
  }
}
